<script setup lang="ts">
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import UserArea from "@/components/ui/UserArea.vue";
import {
  accessStatusLabels,
  providerLabels,
  statusClass,
  subscriptionStatusLabels,
} from "@/libs/adminUsers";
import { dateToUser, dateToUserWithTime, formatCurrency } from "@/libs/filters";
import { gql } from "@apollo/client/core";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
type EditableUser = {
  id?: string;
  name?: string;
  state?: string;
  phone?: string;
  email?: string;
  isAdmin?: boolean;
  freeAccess?: boolean;
  engagementPoints?: number;
  createdAt?: string;
  asaasId?: string;
};

const input = ref<EditableUser>({});
const isAdmin = ref(false);
const freeAccess = ref(false);
const saveError = ref("");
const saved = ref(false);

const {
  loading: loadingUser,
  error,
  result,
  onResult,
  refetch,
} = useQuery(
  gql`
    query AdminUser($id: Key!) {
      adminUser(id: $id) {
        summary {
          user {
            id
            name
            state
            createdAt
            phone
            asaasId
            email
            engagementPoints
            isAdmin
            freeAccess
          }
          accessStatus
          subscriptionStatus
          provider
          validUntil
          nextDueDate
          subscriptionDescription
        }
        subscriptions {
          id
          value
          description
          createdAt
          validFrom
          validUntil
          nextDueDate
          cycle
          subscriptionStatus
          trial
          provider
        }
      }
    }
  `,
  { id: route.params.id },
  { fetchPolicy: "no-cache" }
);

const detail = computed(() => result.value?.adminUser);
const summary = computed(() => detail.value?.summary);
const user = computed(() => summary.value?.user || {});
const subscriptions = computed(() => detail.value?.subscriptions || []);

onResult(({ data }) => {
  const loadedUser = data?.adminUser?.summary?.user;
  if (!loadedUser) return;
  input.value = { ...loadedUser };
  isAdmin.value = !!loadedUser.isAdmin;
  freeAccess.value = !!loadedUser.freeAccess;
});

const { mutate: setUserAdminStatus, loading: setUserAdminStatusLoading } =
  useMutation(gql`
    mutation SetUserAdminStatus($id: Key!, $isAdmin: Boolean!) {
      setUserAdminStatus(id: $id, isAdmin: $isAdmin) {
        id
      }
    }
  `);

const { mutate: setUserFreeAccess, loading: setUserFreeAccessLoading } =
  useMutation(gql`
    mutation SetUserFreeAccess($id: Key!, $freeAccess: Boolean!) {
      setUserFreeAccess(id: $id, freeAccess: $freeAccess) {
        id
      }
    }
  `);

const { mutate: updateUserProfile, loading: updateUserProfileLoading } =
  useMutation(gql`
    mutation UpdateUser($id: Key!, $input: UpdateUserInput!) {
      updateUser(id: $id, input: $input) {
        id
      }
    }
  `);

const saving = computed(
  () =>
    setUserAdminStatusLoading.value ||
    setUserFreeAccessLoading.value ||
    updateUserProfileLoading.value
);

async function saveUser() {
  saveError.value = "";
  saved.value = false;
  const wait: Promise<unknown>[] = [];
  if (isAdmin.value !== user.value.isAdmin) {
    wait.push(
      setUserAdminStatus({ id: user.value.id, isAdmin: isAdmin.value })
    );
  }
  if (freeAccess.value !== user.value.freeAccess) {
    wait.push(
      setUserFreeAccess({ id: user.value.id, freeAccess: freeAccess.value })
    );
  }
  const hasUserChanged =
    input.value.name !== user.value.name ||
    input.value.state !== user.value.state ||
    input.value.phone !== user.value.phone;
  if (hasUserChanged) {
    wait.push(
      updateUserProfile({
        id: user.value.id,
        input: {
          name: input.value.name,
          state: input.value.state,
          phone: input.value.phone,
        },
      })
    );
  }
  try {
    await Promise.all(wait);
    await refetch();
    saved.value = true;
  } catch (_err) {
    saveError.value = "Não foi possível salvar as alterações.";
  }
}

function formatDate(date?: string): string {
  return date ? dateToUserWithTime(date) || "—" : "—";
}

function cycleLabel(cycle?: string): string {
  return cycle === "YEARLY"
    ? "Anual"
    : cycle === "MONTHLY"
    ? "Mensal"
    : cycle || "—";
}
</script>

<template>
  <DefaultLayout>
    <article class="container framework-slot">
      <UserArea title="Detalhes do usuário">
        <router-link class="back-link" to="/adm/users"
          >← Voltar para usuários</router-link
        >

        <div v-if="loadingUser" class="loading-state">Carregando dados...</div>
        <div v-else-if="error" class="alert alert-danger">
          Não foi possível carregar este usuário.
        </div>
        <template v-else-if="summary">
          <section class="user-heading">
            <div>
              <h2>{{ user.name || "Cadastro incompleto" }}</h2>
              <a :href="`mailto:${user.email}`">{{ user.email }}</a>
              <span
                >{{ user.state || "UF não informada" }} · Cadastro em
                {{ formatDate(user.createdAt) }}</span
              >
            </div>
            <div class="heading-badges">
              <span
                class="status-badge"
                :class="statusClass(summary.accessStatus)"
                >{{ accessStatusLabels[summary.accessStatus] }}</span
              >
              <span
                class="status-badge"
                :class="statusClass(summary.subscriptionStatus)"
                >{{
                  subscriptionStatusLabels[summary.subscriptionStatus]
                }}</span
              >
            </div>
          </section>

          <section class="summary-grid" aria-label="Resumo do usuário">
            <div class="summary-item">
              <span>Provedor</span
              ><strong>{{ providerLabels[summary.provider] }}</strong>
            </div>
            <div class="summary-item">
              <span>Validade</span
              ><strong>{{ formatDate(summary.validUntil) }}</strong>
            </div>
            <div class="summary-item">
              <span>Próxima cobrança</span
              ><strong>{{ dateToUser(summary.nextDueDate) }}</strong>
            </div>
            <div class="summary-item">
              <span>Pontos de engajamento</span
              ><strong>{{ user.engagementPoints }}</strong>
            </div>
            <div class="summary-item">
              <span>Asaas</span>
              <a
                v-if="user.asaasId"
                target="_blank"
                rel="noopener"
                :href="`https://www.asaas.com/customerAccount/show/${user.asaasId.replace(
                  /^cus_0*/,
                  ''
                )}`"
                >Abrir cliente ↗</a
              >
              <strong v-else>—</strong>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <div>
                <h3>Histórico de assinaturas</h3>
              </div>
              <span
                >{{ subscriptions.length }}
                {{
                  subscriptions.length === 1 ? "registro" : "registros"
                }}</span
              >
            </div>
            <div v-if="!subscriptions.length" class="empty-state">
              Este usuário ainda não possui assinaturas.
            </div>
            <div v-else class="table-container">
              <table class="table subscription-table" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col">Assinatura</th>
                    <th scope="col">Provedor</th>
                    <th scope="col">Período</th>
                    <th scope="col">Cobrança</th>
                    <th scope="col">Status da assinatura</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  <tr
                    v-for="subscription in subscriptions"
                    :key="subscription.id"
                    role="row"
                  >
                    <td role="cell" data-label="Assinatura">
                      <strong>{{ subscription.description }}</strong
                      ><small>{{ formatDate(subscription.createdAt) }}</small>
                    </td>
                    <td role="cell" data-label="Provedor">
                      {{ providerLabels[subscription.provider] }}
                    </td>
                    <td role="cell" data-label="Período">
                      <span>{{ formatDate(subscription.validFrom) }}</span
                      ><small
                        >até {{ formatDate(subscription.validUntil) }}</small
                      >
                    </td>
                    <td role="cell" data-label="Cobrança">
                      <span
                        >{{ formatCurrency(subscription.value) }} ·
                        {{ cycleLabel(subscription.cycle) }}</span
                      ><small
                        >próxima:
                        {{ dateToUser(subscription.nextDueDate) }}</small
                      >
                    </td>
                    <td role="cell" data-label="Status da assinatura">
                      <span
                        class="status-badge"
                        :class="statusClass(subscription.subscriptionStatus)"
                        >{{
                          subscriptionStatusLabels[
                            subscription.subscriptionStatus
                          ]
                        }}</span
                      >
                      <small v-if="subscription.trial"
                        >Assinatura de teste</small
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <div>
                <h3>Dados e permissões</h3>
              </div>
            </div>
            <div v-if="saveError" class="alert alert-danger">
              {{ saveError }}
            </div>
            <div v-if="saved" class="alert alert-success">
              Usuário atualizado com sucesso.
            </div>
            <form @submit.prevent="saveUser">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="name" class="form-label">Nome</label>
                  <input
                    id="name"
                    v-model="input.name"
                    type="text"
                    class="form-control"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label for="state" class="form-label">Localização</label>
                  <select id="state" v-model="input.state" class="form-select">
                    <option value="">Não informada</option>
                    <option
                      v-for="state in [
                        'AC',
                        'AL',
                        'AP',
                        'AM',
                        'BA',
                        'CE',
                        'DF',
                        'ES',
                        'GO',
                        'MA',
                        'MT',
                        'MS',
                        'MG',
                        'PA',
                        'PB',
                        'PR',
                        'PE',
                        'PI',
                        'RJ',
                        'RN',
                        'RS',
                        'RO',
                        'RR',
                        'SC',
                        'SP',
                        'SE',
                        'TO',
                      ]"
                      :key="state"
                      :value="state"
                    >
                      {{ state }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label for="email" class="form-label">E-mail</label>
                  <input
                    id="email"
                    v-model="input.email"
                    type="email"
                    class="form-control"
                    disabled
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label for="phone" class="form-label">Celular</label>
                  <input
                    id="phone"
                    v-model="input.phone"
                    v-maska="['(##) ####-####', '(##) #####-####']"
                    type="tel"
                    class="form-control"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <label for="isAdmin" class="form-label"
                    >Nível de acesso</label
                  >
                  <select id="isAdmin" v-model="isAdmin" class="form-select">
                    <option :value="false">Usuário</option>
                    <option :value="true">Administrador</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label for="freeAccess" class="form-label"
                    >Acesso livre</label
                  >
                  <select
                    id="freeAccess"
                    v-model="freeAccess"
                    class="form-select"
                  >
                    <option :value="false">Não</option>
                    <option :value="true">Sim</option>
                  </select>
                </div>
              </div>
              <button
                class="btn btn-primary width-200px"
                type="submit"
                :disabled="saving"
              >
                {{ saving ? "Salvando..." : "Salvar" }}
              </button>
            </form>
          </section>
        </template>
      </UserArea>
    </article>
  </DefaultLayout>
</template>

<style scoped>
.back-link {
  display: inline-block;
  margin-bottom: 1rem;
  color: #bfc5a2;
  text-decoration: none;
}
.back-link:hover {
  color: var(--primary-color);
}
.user-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1.15rem;
  border-radius: 0.45rem;
  background: #22231f;
}
.user-heading > div {
  min-width: 0;
  overflow-wrap: anywhere;
}
.user-heading h2 {
  margin: 0 0 0.3rem;
  color: var(--white-ice);
  font-size: 1.35rem;
  font-weight: 500;
}
.user-heading a,
.user-heading span {
  display: block;
  color: #aaa;
}
.heading-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
}
.status-badge {
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  background: #555;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
}
.status-has-access,
.status-active,
.status-trial,
.status-courtesy {
  background: #53680b;
  color: #f1f8d4;
}
.status-no-access {
  background: #8f2929;
  color: #ffe6e6;
}
.status-possibly-overdue {
  background: #7b5a0a;
  color: #fff1c4;
}
.status-cancelled,
.status-expired,
.status-deactivated,
.status-no-subscription {
  background: #4d4d4d;
  color: #e5e5e0;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 1rem;
}
.summary-item {
  overflow-wrap: anywhere;
  min-height: 76px;
  padding: 0.8rem;
  border: 1px solid var(--dark-grey);
  border-radius: 0.4rem;
}
.summary-item span {
  display: block;
  margin-bottom: 0.3rem;
  color: #999;
  font-size: 0.75rem;
  text-transform: uppercase;
}
.summary-item strong,
.summary-item a {
  color: var(--white-ice);
  font-weight: 500;
}
.detail-section {
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--dark-grey);
  border-radius: 0.4rem;
}
.section-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}
.section-heading h3 {
  margin: 0;
  color: var(--white-ice);
  font-size: 1.05rem;
  font-weight: 500;
}
.section-heading p,
.section-heading > span {
  margin: 0.2rem 0 0;
  color: #999;
  font-size: 0.82rem;
}
.table-container {
  border: 1px solid var(--dark-grey);
  border-radius: 0.35rem;
}
.subscription-table {
  width: 100%;
  table-layout: fixed;
  margin-bottom: 0;
  color: var(--white-ice);
}
.subscription-table th,
.subscription-table td {
  overflow-wrap: anywhere;
}
.subscription-table th {
  border-color: #494a46;
  color: #aaa;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}
.subscription-table td {
  border-color: #494a46;
  vertical-align: middle;
}
.subscription-table td strong,
.subscription-table td span,
.subscription-table td small {
  display: block;
}
.subscription-table td small {
  margin-top: 0.2rem;
  color: #999;
}
.loading-state,
.empty-state {
  padding: 2.5rem 1rem;
  border: 1px dashed #555;
  border-radius: 0.4rem;
  color: #aaa;
  text-align: center;
}
@media (max-width: 991.98px) {
  .subscription-table,
  .subscription-table tbody,
  .subscription-table tr,
  .subscription-table td {
    display: block;
    width: 100%;
  }
  .subscription-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .subscription-table tbody {
    border-top: 0;
  }
  .subscription-table tr {
    padding: 0.5rem;
  }
  .subscription-table tr + tr {
    border-top: 1px solid #494a46;
  }
  .subscription-table td {
    border: 0;
  }
  .subscription-table td::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 0.25rem;
    color: #aaa;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
  }
}
@media (max-width: 767.98px) {
  .user-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .heading-badges {
    justify-content: flex-start;
  }
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 479.98px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
